import { read, utils as xlsxUtils } from 'xlsx';
import { v1 as uuid } from 'uuid';
import { isObject, uniq, uniqBy, find, pull } from 'lodash';

import { ANSWER_DEFAULT } from 'containers/QuestionnairePage/constants';

const answersDelimiter = ' - ';

export function convertExcelToCsv(result) {
  const workBook = read(result, { type: 'binary' });

  /* Get first worksheet */
  const name = workBook.SheetNames[0];
  const sheet = workBook.Sheets[name];

  return xlsxUtils.sheet_to_csv(sheet, { header: 1 });
}

export function splitAndFilterEmptyValuesFromText(
  text = '',
  delimiter = /,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/,
) {
  return text.split(delimiter).filter((value) => value.trim() !== '');
}

export function getLabels(text) {
  return splitAndFilterEmptyValuesFromText(text);
}

export function getColumns(lines) {
  const columns = [];

  while (lines.length) {
    const rowArray = splitAndFilterEmptyValuesFromText(lines.shift());

    if (rowArray.length > 0) {
      columns.push(rowArray);
    }
  }

  return columns;
}

export function csvTextToJSON(csvText) {
  const lines = splitAndFilterEmptyValuesFromText(csvText, /\r\n|\n/);
  const headerArray = splitAndFilterEmptyValuesFromText(lines.shift());

  return { columns: getColumns(lines), labels: headerArray };
}

export function validateJSON({ labels, columns }) {
  const labelsLength = labels.length;
  const errors = {
    labelsEmpty: labelsLength === 0,
    columnsEmpty: columns.length === 0,
    differentLength: columns.some((column) => column.length !== labelsLength),
  };

  return {
    errors,
    isValid: !Object.values(errors).includes(true),
  };
}

export function getOptionsArrayFromLevels(levelsTree) {
  const columns = [];
  const traverse =
    (path = []) =>
    (level) => {
      const newPath = [...path, level.value];

      if (level.children) {
        level.children.forEach(traverse(newPath));
      } else {
        columns.push(newPath);
      }
    };

  levelsTree.forEach(traverse());

  return columns;
}

export function getColumnsAndLabelsText(answers, columns, levels) {
  if (answers.length === 0 && columns.length === 0) {
    return {};
  }

  const options = getOptionsArrayFromLevels(levels);

  return {
    labelsText: columns.map(({ beforeLabel }) => beforeLabel).join(','),
    columnsText: options.map((optionArray) => optionArray.join(',')).join('\n'),
  };
}

function getOptionsAddedForColumns(levels, nestedLevel = 0, options = []) {
  const newOptions = [...options];

  newOptions[nestedLevel] = [
    ...(options[nestedLevel] || []),
    ...levels.map(({ value }) => value),
  ];

  return levels.reduce((accumulator, level) => {
    if (level.children && level.children.length > 0) {
      return getOptionsAddedForColumns(
        level.children,
        nestedLevel + 1,
        accumulator,
      );
    }

    return accumulator;
  }, newOptions);
}

export function getNumberOfOptionsAddedForColumns(levels) {
  const optionsPerColumn = getOptionsAddedForColumns(levels);

  return optionsPerColumn.map((options) => uniq(options).length);
}

export function getLevelsWithAnswerRefId(answers) {
  let levelAnswerNextId = 0;
  const answerItems = [...answers];

  return function getLevels(items, parents = []) {
    if (items.length === 0) {
      return [];
    }

    const values = items.reduce((accumulator, arrayValue) => {
      const value = arrayValue[0];
      const newArrayValue = arrayValue.slice(1);
      const lastChild = newArrayValue.length === 0;

      if (accumulator[value] && !lastChild) {
        accumulator[value].push(newArrayValue);
      } else {
        accumulator[value] = lastChild ? [] : [newArrayValue];
      }

      return accumulator;
    }, {});

    return Object.keys(values).map((key) => {
      const children = getLevels(values[key], parents.concat(key));
      const extraProps = {
        children,
      };
      const lastChild = children.length === 0;

      if (lastChild) {
        const fullBody = parents.concat(key).join(answersDelimiter);
        const answer = find(answerItems, ['body', fullBody]);
        pull(answerItems, answer);
        extraProps.children = undefined;
        extraProps.answerRefId = answer.refId;
      }

      levelAnswerNextId += 1;

      return {
        refId: levelAnswerNextId,
        value: key,
        ...extraProps,
      };
    });
  };
}

export function findChildrenOnDepth(level, requestedDepth, depth = 0) {
  if (requestedDepth === depth) {
    return level.children;
  }

  let nestedChildren = [];
  for (let index = 0; index < level.children.length; index += 1) {
    nestedChildren = [
      ...nestedChildren,
      ...findChildrenOnDepth(level.children[index], requestedDepth, depth + 1),
    ];
  }

  return nestedChildren;
}

export function findDepth(level, value, depth = -1) {
  if (!value) {
    return 0;
  }

  if (level.value === value) {
    return depth;
  }

  if (!level.children) {
    return -1;
  }

  let depthLevel;
  for (let index = 0; index < level.children.length; index += 1) {
    depthLevel = findDepth(level.children[index], value, depth + 1);

    if (depthLevel >= 0) {
      break;
    }
  }

  return depthLevel;
}

export function findDrillDownDepthFromConditions(question, conditions) {
  const conditionValue = conditions[0].value;
  const { levels } =
    question.matrix.validation.drillDownConfigurationDto.config;

  return findDepth({ children: levels }, conditionValue);
}

function getAllLevelValues(levels = []) {
  return levels.flatMap(({ value, children }) =>
    [].concat(value, getAllLevelValues(children)),
  );
}

export function isValueExistOnTree(levels) {
  const values = getAllLevelValues(levels);

  return (valueToCheck) => values.includes(valueToCheck);
}

function getAdlConditionValues(answersWithLogic, refId) {
  return answersWithLogic.filter(
    (item) => isObject(item) && item.questionId && item.questionId === refId,
  );
}

export function hasLogicThatWillBreakAfterUpdate({
  refId,
  answersWithLogic,
  updatedLevels,
}) {
  const existedConditionValues = getAdlConditionValues(answersWithLogic, refId);
  const isValueExist = isValueExistOnTree(updatedLevels);

  return existedConditionValues.some(
    (conditionValue) => !isValueExist(conditionValue.value),
  );
}

export function getNewDrillDownState(config, fileName) {
  const { labels, columns } = config;
  const uniqColumns = uniqBy(columns, (item) => item.toString());
  const answers = uniqColumns.map((arrayRecord) => ({
    body: arrayRecord.join(answersDelimiter),
    refId: uuid(),
    type: ANSWER_DEFAULT,
  }));

  const getLevels = getLevelsWithAnswerRefId(answers);
  const updatedLevels = getLevels(uniqColumns);
  const numberOfOptions = getNumberOfOptionsAddedForColumns(updatedLevels);

  return {
    updatedLevels,
    labels,
    answers,
    numberOfOptions,
    fileName,
  };
}
